import ayreneAxios from './ayreneAxios';

export interface MessageUser {
  _id: string;
  username: string;
  avatar: string;
}

export interface Message {
  _id: string;
  sender: MessageUser | string;
  receiver: MessageUser | string;
  content?: string;
  text?: string;
  messageType: string;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: string | null;
  status: 'sent' | 'delivered' | 'read';
  deliveredAt: string | null;
  readAt: string | null;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationParticipant {
  _id: string;
  username: string;
  avatar: string;
  onlineStatus: string;
  lastSeen: string;
}

export interface Conversation {
  _id: string;
  participants: ConversationParticipant[];
  lastMessage: Message | null;
  unreadCount: Record<string, number>;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SendMessageRequest {
  receiverId: string;
  text: string;
}

export interface SendMessageResponse {
  message: Message;
}

export interface GetConversationRequest {
  userId: string;
}

export interface GetConversationResponse {
  success: boolean;
  conversationId: string;
  messages: Message[];
}

export const mergeMessages = (existingMessages: Message[], newMessages: Message[]): Message[] => {
  const messageMap = new Map<string, Message>();

  existingMessages.forEach(msg => {
    messageMap.set(msg._id, msg);
  });

  newMessages.forEach(msg => {
    messageMap.set(msg._id, msg);
  });

  const mergedMessages = Array.from(messageMap.values());

  mergedMessages.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });

  return mergedMessages;
};

export const validateMessage = (content: string): { isValid: boolean; error?: string } => {
  const trimmedContent = content.trim();

  if (!trimmedContent) {
    return { isValid: false, error: 'Message cannot be empty' };
  }

  if (trimmedContent.length > 2000) {
    return { isValid: false, error: 'Message is too long (max 2000 characters)' };
  }

  return { isValid: true };
};

export const sendMessage = async (receiverId: string, content: string): Promise<SendMessageResponse> => {
  try {
    const validation = validateMessage(content);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    const trimmedContent = content.trim();

    console.log('📤 Sending message to receiver:', receiverId);

    const response = await ayreneAxios.post<SendMessageResponse>(
      '/messages/send',
      {
        receiverId,
        text: trimmedContent
      }
    );

    console.log('✅ Message sent successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to send message:', error);
    throw error;
  }
};

export const getConversation = async (userId: string): Promise<GetConversationResponse> => {
  try {
    console.log('🔄 Fetching conversations for user:', userId);

    const response = await ayreneAxios.post<GetConversationResponse>(
      '/messages/messages',
      {
        userId
      }
    );

    console.log('✅ Conversations fetched successfully');
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch conversations:', error);
    throw error;
  }
};

export const findConversationByReceiver = (conversations: Conversation[], receiverId: string, currentUserId: string): string | null => {
  const conversation = conversations.find(conv => {
    const participantIds = conv.participants.map(p => p._id);
    return participantIds.includes(receiverId) && participantIds.includes(currentUserId);
  });

  return conversation ? conversation._id : null;
};

export const messageService = {
  sendMessage,
  getConversation,
  validateMessage,
  findConversationByReceiver,
  mergeMessages
};
